// BUILD-046 Owner direction of 2026-10-21 (cycle A, correction B "consistent overdue"): WHICH of the Caller's own tasks My Tasks starts from, before the quick filter, the
// search and the sort.
//
// Normally My Tasks shows one STATUS at a time (Active, Waiting, Blocked or Completed): the status the Caller chose in the Status menu. Overdue is different. It is offered in
// the same menu, with a count, but it is not a status: an overdue task can be Active, Waiting or Blocked. So when Overdue is chosen - from the Status menu, or as Filter >
// Overdue - the list starts from EVERY task (the status tab is not applied), the one overdue rule (overdueRule.ts) keeps the eligible ones, and each row still shows its own
// status. The list is then exactly the tasks the Overdue count counted: Active + Waiting + Blocked, never only the Active tab.
//
// Pure module. Type-only knowledge of a task: its `status`. Erasable TypeScript only.

/** The quick filter value that means Overdue (sortModel.ts QuickFilter 'overdue'). */
export const OVERDUE_FILTER = 'overdue';

/** The Status-menu key of the Overdue choice (viewCountsModel.ts figure key). */
export const OVERDUE_STATUS_KEY = 'mine-overdue';

/** True when the quick filter is Overdue. */
export function overdueSelected(quickFilter: string): boolean {
  return quickFilter === OVERDUE_FILTER;
}

/** BUILD-046 G4: the quick filter value that means Follow-up due, and its Status-menu key. Like Overdue it is a DATE condition over every unfinished status, not a status, so the list starts from every task. */
export const FOLLOW_UP_DUE_FILTER = 'follow-up-due';
export const FOLLOW_UP_DUE_STATUS_KEY = 'mine-follow-up-due';
export function followUpDueSelected(quickFilter: string): boolean {
  return quickFilter === FOLLOW_UP_DUE_FILTER;
}

/** The Status-menu value of the combined open view (2026-10-24 Owner correction 1): Active, In progress, Waiting and Blocked together. It is a view, not a stored status. */
export const ALL_OPEN = 'open';

/** The unfinished statuses All open includes. Completed and cancelled are not among them. */
export const OPEN_STATUSES: readonly string[] = Object.freeze(['active', 'in_progress', 'waiting', 'blocked']);

/** True for an unfinished task: one of the four open statuses and not cancelled or ended. */
export function isOpenTask(item: { status?: unknown; lifecycle?: unknown }): boolean {
  return typeof item.status === 'string' && OPEN_STATUSES.includes(item.status) && item.lifecycle !== 'cancelled' && item.lifecycle !== 'ended';
}

/** The tasks My Tasks starts from: all of them while Overdue is selected, every unfinished one for All open, otherwise the tasks of the chosen status tab. A new array. */
export function mineScope<T extends { status?: unknown; lifecycle?: unknown }>(items: readonly T[], activeTab: string, quickFilter: string): T[] {
  if (overdueSelected(quickFilter) || followUpDueSelected(quickFilter)) return [...items];
  if (activeTab === ALL_OPEN) return items.filter((item) => isOpenTask(item));
  return items.filter((item) => item.status === activeTab);
}

/** The Status-menu key shown as selected: Overdue while it is selected (however it was chosen), else the chosen status. */
export function mineStatusKey(activeTab: string, quickFilter: string): string {
  return overdueSelected(quickFilter) ? OVERDUE_STATUS_KEY : followUpDueSelected(quickFilter) ? FOLLOW_UP_DUE_STATUS_KEY : `mine-${activeTab}`;
}

/** The accessible name of the My Tasks table: "My overdue tasks" while Overdue is selected, else "My <status> tasks". */
export function mineTableLabel(activeTab: string, quickFilter: string): string {
  return `My ${overdueSelected(quickFilter) ? 'overdue' : followUpDueSelected(quickFilter) ? 'follow-up due' : activeTab.replace('_', ' ')} tasks`; // 'open' reads "My open tasks"
}
